import { useState } from 'react';
import { ChevronLeft, Eye, UserX, X } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import { TeamSelect } from '../../components/TeamSelect';
import {
  Button,
  Card,
  CardHead,
  ConfirmDialog,
  EmptyState,
  Field,
  FilePicker,
  Input,
  Select,
  SkeletonText,
} from '../../components/ui';
import {
  describeMissing,
  useCharacter,
  useCurrentUser,
  useDeleteCharacter,
  useUpdateCharacter,
  useUploadAvatar,
} from '../../hooks';
import type { Id } from '../../types/api';
import { CharacterAvatar } from './CharacterAvatar';
import { CharacterVoices } from './CharacterVoices';
import {
  CHARACTER_CATEGORIES,
  CHARACTER_CATEGORY_LABELS,
  type CharacterCategory,
} from '../../types/api';
import { teamLabel } from '../../lib/team';
import { LANGUAGE_OPTIONS, formatDate, languageLabel } from './lib';
import styles from './Characters.module.css';

interface Props {
  characterId: Id;
  canEdit: boolean;
  onClose: () => void;
}

export function CharacterDetail({ characterId, canEdit: roleCanEdit, onClose }: Props) {
  const character = useCharacter(characterId);
  const { canChange, isAdmin } = useCurrentUser();

  const update = useUpdateCharacter(characterId);
  const remove = useDeleteCharacter();
  const uploadAvatar = useUploadAvatar(characterId);

  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('ru');
  const [category, setCategory] = useState<CharacterCategory | ''>('');

  if (character.isLoading) {
    return <SkeletonText lines={3} />;
  }

  if (character.error) {
    const notFound = character.error.isNotFound;
    return (
      <div className={styles.page}>
        <BackRow onClose={onClose} />
        {notFound ? (
          <Card>
            <EmptyState
              icon={<UserX size={18} strokeWidth={1.5} />}
              title="Персонаж не найден"
              description="Такого персонажа нет или он не ваш."
              action={
                <Button size="sm" onClick={onClose}>
                  Все персонажи
                </Button>
              }
            />
          </Card>
        ) : (
          <ErrorBanner error={character.error} onRetry={() => void character.refetch()} />
        )}
      </div>
    );
  }

  const item = character.data;
  if (!item) return null;
  const canEdit = roleCanEdit && canChange(item);

  const missing = describeMissing(item);

  const startEditing = () => {
    setName(item.name);
    setLanguage(item.language);
    setCategory(item.category ?? '');
    setEditing(true);
  };

  return (
    <div className={styles.page}>
      <BackRow onClose={onClose} title={item.name} />

      {update.error && <ErrorBanner error={update.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}
      {uploadAvatar.error && <ErrorBanner error={uploadAvatar.error} />}

      <div className={styles.detailGrid}>
        <Card>
          <div className={styles.sideHead}>
            <CharacterAvatar character={item} kind="circle" />
            {editing ? (
              <div className={styles.sideForm}>
                <Field layout="stacked" label="Имя персонажа">
                  {(id) => (
                    <Input
                      id={id}
                      type="text"
                      value={name}
                      autoFocus
                      maxLength={200}
                      onChange={(event) => setName(event.target.value)}
                    />
                  )}
                </Field>
                <Field layout="stacked" label="Язык">
                  {(id) => (
                    <Select
                      id={id}
                      value={language}
                      onChange={(event) => setLanguage(event.target.value)}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                      {!LANGUAGE_OPTIONS.some((option) => option.code === item.language) && (
                        <option value={item.language}>{item.language}</option>
                      )}
                    </Select>
                  )}
                </Field>
                <Field layout="stacked" label="Категория">
                  {(id) => (
                    <Select
                      id={id}
                      value={category}
                      onChange={(event) => setCategory(event.target.value as CharacterCategory | '')}
                    >
                      <option value="">не указан</option>
                      {CHARACTER_CATEGORIES.map((value) => (
                        <option key={value} value={value}>
                          {CHARACTER_CATEGORY_LABELS[value]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <div className={styles.sideFormActions}>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={update.isPending || name.trim().length === 0}
                    onClick={() =>
                      update.mutate(
                        { name: name.trim(), language, category: category || null },
                        { onSuccess: () => setEditing(false) },
                      )
                    }
                  >
                    {update.isPending ? 'Сохраняем…' : 'Сохранить'}
                  </Button>
                  <Button size="sm" onClick={() => setEditing(false)}>
                    Отмена
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.detailName}>{item.name}</div>
                <div className={styles.detailSub}>
                  {item.ready ? 'готов к работе' : `не готов: ${missing}`}
                </div>
              </>
            )}
          </div>

          <div className={styles.kv}>
            <div className={styles.kvKey}>Язык</div>
            <div className={styles.kvValue}>{languageLabel(item.language)}</div>
            <div className={styles.kvKey}>Категория</div>
            <div className={styles.kvValue}>
              {item.category ? CHARACTER_CATEGORY_LABELS[item.category] : 'не указан'}
            </div>
            <div className={styles.kvKey}>Команда</div>
            <div className={styles.kvValue}>
              {isAdmin ? (
                <TeamSelect
                  value={item.team}
                  disabled={update.isPending}
                  onChange={(team) => update.mutate({ team })}
                />
              ) : (
                teamLabel(item.team)
              )}
            </div>
            <div className={styles.kvKey}>Автор</div>
            <div className={styles.kvValue}>{item.author_login ?? 'не известен'}</div>
            <div className={styles.kvKey}>Голосов</div>
            <div className={styles.kvValue}>{item.voices.length}</div>
            <div className={styles.kvKey}>Создан</div>
            <div className={styles.kvValue}>{formatDate(item.created_at)}</div>
            <div className={styles.kvKey}>Изменён</div>
            <div className={styles.kvValue}>{formatDate(item.updated_at)}</div>
          </div>

          <div className={styles.sideActions}>
            {!canEdit && (
              <div className={styles.readonlyNote}>
                <Eye size={13} strokeWidth={1.5} />
                {roleCanEdit
                  ? 'Менять и удалять персонажа может только автор или администратор.'
                  : 'Роль «наблюдатель»: карточку видно целиком, менять данные нельзя.'}
              </div>
            )}

            {canEdit && !editing && (
              <Button size="sm" onClick={startEditing}>
                Редактировать
              </Button>
            )}

            {canEdit && (
              <Button size="sm" variant="danger" onClick={() => setConfirming(true)}>
                Удалить персонажа
              </Button>
            )}
          </div>
        </Card>

        <div className={styles.detailColumn}>
          <Card>
            <CardHead
              title={<span className="eyebrow">видео персонажа</span>}
              hint="исходник, из которого берут говорящую голову"
              actions={
                canEdit ? (
                  <FilePicker
                    file={null}
                    onPick={(file) => file && uploadAvatar.mutate(file)}
                    accept="video/*,image/*"
                    disabled={uploadAvatar.isPending}
                    buttonLabel={
                      uploadAvatar.isPending
                        ? 'Загружаем…'
                        : item.has_avatar
                          ? 'Заменить видео'
                          : 'Добавить видео'
                    }
                    emptyLabel=""
                    ariaLabel="Видео персонажа"
                  />
                ) : null
              }
            />
            <div className={styles.panelBody}>
              <CharacterAvatar character={item} kind="player" />
            </div>
          </Card>

          <CharacterVoices character={item} canEdit={roleCanEdit} />

        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        destructive
        busy={remove.isPending}
        title="Удалить персонажа?"
        body={
          <>
            Вместе с ним пропадут его голоса ({item.voices.length}), их образцы и видео.
            Голоса удалятся вместе с ним. Отменить нельзя.
          </>
        }
        confirmLabel="Удалить"
        onCancel={() => setConfirming(false)}
        onConfirm={() => remove.mutate(characterId, { onSuccess: onClose })}
      />
    </div>
  );
}

function BackRow({ onClose, title }: { onClose: () => void; title?: string }) {
  return (
    <div className={styles.detailHead}>
      <Button size="sm" onClick={onClose}>
        <ChevronLeft size={14} strokeWidth={1.6} />
        Все персонажи
      </Button>
      {title && <span className={styles.detailCrumb}>{title}</span>}
      <Button
        size="sm"
        iconOnly
        className={styles.pushRight}
        onClick={onClose}
        title="Закрыть карточку"
        aria-label="Закрыть карточку"
      >
        <X size={14} strokeWidth={1.6} />
      </Button>
    </div>
  );
}
